import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Page from "../Components/Page";
import { axiosInstance } from "../Components/axiosInstance.js";

const DATA_SCOPE_OPTIONS = [
  { value: "farmers", label: "Grower profile" },
  { value: "farms", label: "Farms" },
  { value: "fields", label: "Fields" },
  { value: "crop_years", label: "Crop years" },
  { value: "fertilizers", label: "Fertilizer" },
  { value: "equipment", label: "Equipment" },
  { value: "4r", label: "4R" },
  { value: "analysis", label: "Analysis" },
  { value: "holos", label: "Holos" },
];
const EXPORTABLE_SCOPE_OPTIONS = DATA_SCOPE_OPTIONS.filter((option) =>
  ["farmers", "farms", "fields", "crop_years"].includes(option.value)
);

const DEFAULT_FORM = {
  name: "",
  research_purpose: "",
  reward_summary: "",
  criteria_summary: "",
  provinces: "",
  crops: "",
  min_crop_years: "0",
  require_analysis_ready: false,
  requires_admin_approval: true,
  data_scope: ["farmers", "farms", "fields", "crop_years"],
  application_open_at: "",
  starts_at: "",
  ends_at: "",
  partner_logo_small_url: "",
  partner_logo_large_url: "",
};

function formatDate(value) {
  if (!value) {
    return "Not set";
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "Not set";
  }
  return parsed.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatDateTimeLocal(value) {
  if (!value) {
    return "";
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "";
  }
  const offsetMs = parsed.getTimezoneOffset() * 60000;
  return new Date(parsed.getTime() - offsetMs).toISOString().slice(0, 16);
}

function parseCsv(value) {
  return String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function toIsoOrNull(value) {
  if (!value) {
    return null;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
}

function getDownloadFilename(response, fallback) {
  const disposition = response?.headers?.["content-disposition"];
  const match = disposition?.match(/filename="?([^";]+)"?/i);
  return match?.[1] || fallback;
}

function buildForm(project) {
  if (!project) {
    return { ...DEFAULT_FORM };
  }
  const criteria = project.criteria || {};
  return {
    name: project.name || "",
    research_purpose: project.research_purpose || "",
    reward_summary: project.reward_summary || "",
    criteria_summary: project.criteria_summary || "",
    provinces: Array.isArray(criteria.provinces) ? criteria.provinces.join(", ") : "",
    crops: Array.isArray(criteria.crops) ? criteria.crops.join(", ") : "",
    min_crop_years: String(criteria.min_crop_years ?? 0),
    require_analysis_ready: Boolean(criteria.require_analysis_ready),
    requires_admin_approval: project.requires_admin_approval !== false,
    data_scope: Array.isArray(project.data_scope) ? project.data_scope : DEFAULT_FORM.data_scope,
    application_open_at: formatDateTimeLocal(project.application_open_at),
    starts_at: formatDateTimeLocal(project.starts_at),
    ends_at: formatDateTimeLocal(project.ends_at),
    partner_logo_small_url: project.partner_logo_small_url || "",
    partner_logo_large_url: project.partner_logo_large_url || "",
  };
}

function buildPayload(form) {
  return {
    name: form.name.trim(),
    research_purpose: form.research_purpose.trim() || null,
    reward_summary: form.reward_summary.trim() || null,
    criteria_summary: form.criteria_summary.trim() || null,
    criteria: {
      provinces: parseCsv(form.provinces),
      crops: parseCsv(form.crops),
      min_crop_years: Number(form.min_crop_years || 0),
      require_analysis_ready: Boolean(form.require_analysis_ready),
    },
    data_scope: form.data_scope,
    requires_admin_approval: Boolean(form.requires_admin_approval),
    application_open_at: toIsoOrNull(form.application_open_at),
    starts_at: toIsoOrNull(form.starts_at),
    ends_at: toIsoOrNull(form.ends_at),
    partner_logo_small_url: form.partner_logo_small_url.trim() || null,
    partner_logo_large_url: form.partner_logo_large_url.trim() || null,
  };
}

function getStatusClass(status) {
  const value = String(status || "").toUpperCase();
  if (value === "ACTIVE") {
    return "border-[#34a853] bg-[#eef8f0] text-[#1f7a3a]";
  }
  if (value === "CLOSED") {
    return "border-[#8a94a6] bg-[#f2f4f7] text-[#435067]";
  }
  return "border-[#d8a31a] bg-[#fff8e5] text-[#8a5a00]";
}

function ProjectStatusBadge({ status }) {
  return (
    <span className={`inline-flex min-w-[78px] justify-center rounded-[4px] border px-2 py-1 text-xs font-semibold uppercase ${getStatusClass(status)}`}>
      {status || "Draft"}
    </span>
  );
}

function Field({ label, children }) {
  return (
    <label className="flex flex-col gap-1 text-sm font-semibold text-[#4d5968]">
      {label}
      {children}
    </label>
  );
}

function textInputClass() {
  return "min-h-[42px] rounded-[4px] border border-[#cfd6df] bg-white px-3 py-2 text-base font-normal text-[#243245] outline-none focus:border-[#00adee] focus:ring-2 focus:ring-[#00adee]/20";
}

function ProjectForm({
  errorMessage,
  initialProject,
  isSaving,
  onCancel,
  onSubmit,
}) {
  const [form, setForm] = useState(() => buildForm(initialProject));

  useEffect(() => {
    setForm(buildForm(initialProject));
  }, [initialProject]);

  function setField(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  function toggleScope(scope) {
    setForm((current) => {
      const selected = current.data_scope.includes(scope)
        ? current.data_scope.filter((item) => item !== scope)
        : [...current.data_scope, scope];
      return { ...current, data_scope: selected };
    });
  }

  function handleSubmit(event) {
    event.preventDefault();
    onSubmit(buildPayload(form));
  }

  const applicationOpen = form.application_open_at
    ? new Date(form.application_open_at).getTime()
    : null;
  const startsAt = form.starts_at ? new Date(form.starts_at).getTime() : null;
  const endsAt = form.ends_at ? new Date(form.ends_at).getTime() : null;
  const timelineError =
    applicationOpen && startsAt && applicationOpen > startsAt
      ? "Application opening must be before the project start."
      : startsAt && endsAt && startsAt > endsAt
        ? "Project start must be before the project end."
        : applicationOpen && endsAt && applicationOpen > endsAt
          ? "Application opening must be before the project end."
          : "";
  const canSave =
    form.name.trim() &&
    form.data_scope.length > 0 &&
    !timelineError &&
    !isSaving;

  return (
    <form className="flex w-full flex-col gap-5" onSubmit={handleSubmit}>
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1.15fr)_minmax(360px,0.85fr)]">
        <section className="rounded-[8px] border border-slate-200 bg-white p-5 shadow-[0_10px_24px_rgba(15,23,42,0.04)]">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field label="Project name">
              <input className={textInputClass()} onChange={(event) => setField("name", event.target.value)} required value={form.name} />
            </Field>
            <Field label="Minimum crop years">
              <input className={textInputClass()} min="0" onChange={(event) => setField("min_crop_years", event.target.value)} type="number" value={form.min_crop_years} />
            </Field>
            <Field label="Research purpose">
              <textarea className={`${textInputClass()} min-h-[120px] resize-y`} onChange={(event) => setField("research_purpose", event.target.value)} value={form.research_purpose} />
            </Field>
            <Field label="Reward summary">
              <textarea className={`${textInputClass()} min-h-[120px] resize-y`} onChange={(event) => setField("reward_summary", event.target.value)} value={form.reward_summary} />
            </Field>
            <Field label="Criteria summary">
              <textarea className={`${textInputClass()} min-h-[92px] resize-y`} onChange={(event) => setField("criteria_summary", event.target.value)} value={form.criteria_summary} />
            </Field>
            <div className="grid grid-cols-1 gap-4">
              <Field label="Eligible provinces">
                <input className={textInputClass()} onChange={(event) => setField("provinces", event.target.value)} placeholder="SK, AB" value={form.provinces} />
              </Field>
              <Field label="Eligible crops">
                <input className={textInputClass()} onChange={(event) => setField("crops", event.target.value)} placeholder="Canola, Wheat" value={form.crops} />
              </Field>
            </div>
          </div>
        </section>

        <section className="rounded-[8px] border border-slate-200 bg-white p-5 shadow-[0_10px_24px_rgba(15,23,42,0.04)]">
          <div className="grid grid-cols-1 gap-4">
            <label className="flex items-center gap-3 text-sm font-semibold text-[#4d5968]">
              <input checked={form.require_analysis_ready} className="h-4 w-4" onChange={(event) => setField("require_analysis_ready", event.target.checked)} type="checkbox" />
              Require analysis-ready crop years
            </label>
            <label className="rounded-[6px] border border-slate-200 bg-slate-50 p-3 text-sm text-[#4d5968]">
              <span className="flex items-center gap-3 font-semibold">
                <input
                  checked={form.requires_admin_approval}
                  className="h-4 w-4"
                  onChange={(event) => setField("requires_admin_approval", event.target.checked)}
                  type="checkbox"
                />
                Require administrator approval
              </span>
              <span className="mt-2 block pl-7 text-xs font-normal leading-5 text-slate-500">
                When disabled, eligible growers join immediately after accepting the project terms.
              </span>
            </label>
            <Field label="Application opens">
              <input className={textInputClass()} onChange={(event) => setField("application_open_at", event.target.value)} type="datetime-local" value={form.application_open_at} />
            </Field>
            <Field label="Project starts">
              <input className={textInputClass()} onChange={(event) => setField("starts_at", event.target.value)} type="datetime-local" value={form.starts_at} />
            </Field>
            <Field label="Project ends">
              <input className={textInputClass()} onChange={(event) => setField("ends_at", event.target.value)} type="datetime-local" value={form.ends_at} />
            </Field>
            <Field label="Small logo URL">
              <input className={textInputClass()} onChange={(event) => setField("partner_logo_small_url", event.target.value)} value={form.partner_logo_small_url} />
            </Field>
            <Field label="Large logo URL">
              <input className={textInputClass()} onChange={(event) => setField("partner_logo_large_url", event.target.value)} value={form.partner_logo_large_url} />
            </Field>
          </div>
        </section>
      </div>

      <section className="rounded-[8px] border border-slate-200 bg-white p-5 shadow-[0_10px_24px_rgba(15,23,42,0.04)]">
        <p className="text-sm font-semibold uppercase tracking-[0.14em] text-[#75839a]">Data Scope</p>
        <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
          {DATA_SCOPE_OPTIONS.map((option) => (
            <label className={`flex min-h-[44px] items-center gap-2 rounded-[4px] border px-3 text-sm font-semibold ${form.data_scope.includes(option.value) ? "border-[#34a853] bg-[#eef8f0] text-[#1f6d38]" : "border-[#d7dde6] bg-white text-[#586579]"}`} key={option.value}>
              <input checked={form.data_scope.includes(option.value)} onChange={() => toggleScope(option.value)} type="checkbox" />
              {option.label}
            </label>
          ))}
        </div>
      </section>

      {timelineError || errorMessage ? (
        <div
          className="rounded-[6px] border border-[#f2b8b5] bg-[#fff1f0] px-4 py-3 text-sm font-semibold text-[#9b1c13]"
          role="alert"
        >
          {timelineError || errorMessage}
        </div>
      ) : null}

      <div className="flex flex-wrap justify-end gap-3">
        <button className="min-h-[42px] rounded-[4px] border border-[#b9c1cc] bg-white px-5 font-semibold text-[#344054]" onClick={onCancel} type="button">
          Cancel
        </button>
        <button className="min-h-[42px] rounded-[4px] bg-[#34a853] px-5 font-semibold text-white disabled:bg-[#c9ced6]" disabled={!canSave} type="submit">
          {isSaving ? "Saving..." : "Save Project"}
        </button>
      </div>
    </form>
  );
}

function EmptyState() {
  return (
    <div className="rounded-[8px] border border-dashed border-[#b8c3d1] bg-white p-8 text-center text-[#5b6778]">
      No partner projects yet.
    </div>
  );
}

export default function PartnerProjectsPage({ mode = "list" }) {
  const { projectPublicId } = useParams();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [projectDetail, setProjectDetail] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [selectedDataScope, setSelectedDataScope] = useState(["farmers", "farms", "fields", "crop_years"]);
  const [updatedSince, setUpdatedSince] = useState("");
  const [dataSummary, setDataSummary] = useState(null);
  const [syncPreview, setSyncPreview] = useState(null);
  const [dataState, setDataState] = useState("");

  const isCreating = mode === "create";
  const isEditing = mode === "edit";
  const selectedProject = useMemo(
    () => projectDetail || projects.find((project) => project.public_id === projectPublicId),
    [projectDetail, projectPublicId, projects],
  );

  async function loadProjects() {
    setIsLoading(true);
    setError("");
    try {
      const response = await axiosInstance.get("/partner/projects");
      setProjects(Array.isArray(response.data) ? response.data : []);
    } catch (requestError) {
      setProjects([]);
      setError(requestError?.response?.data?.detail || "Unable to load partner projects.");
    } finally {
      setIsLoading(false);
    }
  }

  async function loadProjectDetail(publicId) {
    if (!publicId) {
      setProjectDetail(null);
      return;
    }
    setError("");
    try {
      const response = await axiosInstance.get(`/partner/projects/${publicId}`);
      setProjectDetail(response.data);
    } catch (requestError) {
      setProjectDetail(null);
      setError(requestError?.response?.data?.detail || "Unable to load project details.");
    }
  }

  useEffect(() => {
    loadProjects();
  }, []);

  useEffect(() => {
    loadProjectDetail(projectPublicId);
  }, [projectPublicId]);

  const selectedProjectScopeKey = (selectedProject?.data_scope || []).join("|");

  useEffect(() => {
    const projectScope = selectedProjectScopeKey ? selectedProjectScopeKey.split("|") : [];
    const allowed = EXPORTABLE_SCOPE_OPTIONS
      .map((option) => option.value)
      .filter((value) => projectScope.length === 0 || projectScope.includes(value));
    setSelectedDataScope(allowed.length > 0 ? allowed : ["farmers", "farms", "fields", "crop_years"]);
    setUpdatedSince("");
    setDataSummary(null);
    setSyncPreview(null);
    setDataState("");
  }, [selectedProject?.public_id, selectedProjectScopeKey]);

  async function refreshAfterChange(project, message) {
    setNotice(message);
    setProjectDetail(project);
    await loadProjects();
  }

  async function handleCreate(payload) {
    setIsSaving(true);
    setError("");
    try {
      const response = await axiosInstance.post("/partner/projects", payload);
      await refreshAfterChange(response.data, "Project saved as draft.");
      navigate(`/projects/${response.data.public_id}`);
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || "Unable to create project.");
    } finally {
      setIsSaving(false);
    }
  }

  async function handleUpdate(payload) {
    if (!projectPublicId) {
      return;
    }
    setIsSaving(true);
    setError("");
    try {
      const response = await axiosInstance.patch(`/partner/projects/${projectPublicId}`, payload);
      await refreshAfterChange(response.data, "Project updated.");
      navigate(`/projects/${projectPublicId}`);
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || "Unable to update project.");
    } finally {
      setIsSaving(false);
    }
  }

  async function runAction(action, successMessage) {
    if (!projectPublicId) {
      return;
    }
    setIsSaving(true);
    setError("");
    try {
      const response = await axiosInstance.post(`/partner/projects/${projectPublicId}/${action}`);
      await refreshAfterChange(response.data, successMessage);
      await loadProjectDetail(projectPublicId);
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || "Unable to update project status.");
    } finally {
      setIsSaving(false);
    }
  }

  function toggleSelectedDataScope(scope) {
    setSelectedDataScope((current) =>
      current.includes(scope)
        ? current.filter((item) => item !== scope)
        : [...current, scope]
    );
  }

  function buildDataParams(limit = 200) {
    const params = {
      include: selectedDataScope.join(","),
      limit,
    };
    const updatedSinceIso = toIsoOrNull(updatedSince);
    if (updatedSinceIso) {
      params.updated_since = updatedSinceIso;
    }
    return params;
  }

  async function refreshDataPreview() {
    if (!selectedProject?.public_id || selectedDataScope.length === 0) {
      return;
    }
    setDataState("preview");
    setError("");
    try {
      const response = await axiosInstance.get(
        `/partner/projects/${selectedProject.public_id}/data/sync`,
        { params: buildDataParams(200) }
      );
      setSyncPreview(response.data);
      setDataSummary({
        counts: Object.fromEntries(
          Object.entries(response.data?.data || {}).map(([key, entity]) => [key, entity.count || 0])
        ),
      });
    } catch (requestError) {
      setSyncPreview(null);
      setDataSummary(null);
      setError(requestError?.response?.data?.detail || "Unable to load project data preview.");
    } finally {
      setDataState("");
    }
  }

  async function exportProjectData() {
    if (!selectedProject?.public_id || selectedDataScope.length === 0) {
      return;
    }
    setDataState("export");
    setError("");
    try {
      const response = await axiosInstance.get(
        `/partner/projects/${selectedProject.public_id}/data/export`,
        {
          params: buildDataParams(5000),
          responseType: "blob",
        }
      );
      downloadBlob(
        response.data,
        getDownloadFilename(response, `fieldprint-partner-project-${selectedProject.public_id}-data.xlsx`)
      );
      setNotice("Project data exported.");
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || "Unable to export project data.");
    } finally {
      setDataState("");
    }
  }

  const activeCount = projects.filter((project) => project.status === "ACTIVE").length;
  const participantCount = projects.reduce((total, project) => total + Number(project.counts?.opted_in || 0), 0);
  const headerActions = (
    <Link className="inline-flex min-h-[42px] items-center gap-2 rounded-[4px] bg-[#34a853] px-4 text-sm font-semibold text-white no-underline shadow-[0_8px_18px_rgba(52,168,83,0.22)]" to="/projects/new">
      <AddIcon fontSize="small" />
      New Project
    </Link>
  );

  return (
    <Page title="Projects" headerActions={headerActions} headerBorderColor="border-[#34a853]" headerVariant="hybrid" contentWidth="wide" padding="p-4 md:p-6" introContent="Create and manage partner research projects, participation windows, consented data requirements, and subscription status.">
      <div className="flex w-full flex-col gap-5 text-[#5c6673]">
        {error ? <div className="rounded-[6px] border border-[#f2b8b5] bg-[#fff1f0] px-4 py-3 text-[#9b1c13]">{error}</div> : null}
        {notice ? <div className="rounded-[6px] border border-[#a7d7b7] bg-[#eef8f0] px-4 py-3 text-[#1f6d38]">{notice}</div> : null}

        {isCreating || isEditing ? (
          <ProjectForm errorMessage={error} initialProject={isEditing ? selectedProject : null} isSaving={isSaving} onCancel={() => navigate(projectPublicId ? `/projects/${projectPublicId}` : "/projects")} onSubmit={isCreating ? handleCreate : handleUpdate} />
        ) : (
          <>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className="rounded-[8px] border border-slate-200 bg-white p-5 shadow-[0_10px_24px_rgba(15,23,42,0.04)]">
                <p className="text-sm font-semibold uppercase tracking-[0.14em] text-[#75839a]">Total Projects</p>
                <p className="mt-2 text-3xl font-light text-[#243245]">{isLoading ? "..." : projects.length}</p>
              </div>
              <div className="rounded-[8px] border border-slate-200 bg-white p-5 shadow-[0_10px_24px_rgba(15,23,42,0.04)]">
                <p className="text-sm font-semibold uppercase tracking-[0.14em] text-[#75839a]">Active</p>
                <p className="mt-2 text-3xl font-light text-[#243245]">{isLoading ? "..." : activeCount}</p>
              </div>
              <div className="rounded-[8px] border border-slate-200 bg-white p-5 shadow-[0_10px_24px_rgba(15,23,42,0.04)]">
                <p className="text-sm font-semibold uppercase tracking-[0.14em] text-[#75839a]">Subscribed</p>
                <p className="mt-2 text-3xl font-light text-[#243245]">{isLoading ? "..." : participantCount}</p>
              </div>
            </div>

            <div>
              {!selectedProject ? (
              <section className="overflow-hidden rounded-[8px] border border-slate-200 bg-white shadow-[0_10px_24px_rgba(15,23,42,0.04)]">
                <div className="border-b border-slate-200 px-5 py-4">
                  <p className="text-sm font-semibold uppercase tracking-[0.14em] text-[#75839a]">Project Workspace</p>
                </div>
                {isLoading ? (
                  <div className="p-6">Loading projects...</div>
                ) : projects.length === 0 ? (
                  <div className="p-5"><EmptyState /></div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[780px] border-collapse text-left text-sm">
                      <thead className="bg-[#f7f8f6] text-xs uppercase tracking-[0.12em] text-[#667085]">
                        <tr>
                          <th className="px-5 py-3 font-semibold">Project</th>
                          <th className="px-5 py-3 font-semibold">Status</th>
                          <th className="px-5 py-3 font-semibold">Window</th>
                          <th className="px-5 py-3 font-semibold">Subscribed</th>
                          <th className="px-5 py-3 font-semibold">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {projects.map((project) => (
                          <tr className={`border-t border-slate-200 ${project.public_id === projectPublicId ? "bg-[#f4faf6]" : "bg-white"}`} key={project.public_id}>
                            <td className="px-5 py-4">
                              <p className="font-semibold text-[#243245]">{project.name}</p>
                              <p className="mt-1 text-xs text-[#75839a]">{project.public_id}</p>
                            </td>
                            <td className="px-5 py-4"><ProjectStatusBadge status={project.status} /></td>
                            <td className="px-5 py-4">{formatDate(project.starts_at)} - {formatDate(project.ends_at)}</td>
                            <td className="px-5 py-4">
                              <span className="font-semibold text-[#243245]">{project.counts?.opted_in || 0}</span>
                              <span className="text-[#75839a]"> / {project.counts?.total || 0}</span>
                            </td>
                            <td className="px-5 py-4">
                              <div className="flex gap-2">
                                <Link className="inline-flex h-9 w-9 items-center justify-center rounded-[4px] border border-[#cfd6df] text-[#435067]" title="View" to={`/projects/${project.public_id}`}>
                                  <VisibilityIcon fontSize="small" />
                                </Link>
                                <Link className="inline-flex h-9 w-9 items-center justify-center rounded-[4px] border border-[#cfd6df] text-[#435067]" title="Edit" to={`/projects/${project.public_id}/edit`}>
                                  <EditIcon fontSize="small" />
                                </Link>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
              ) : null}

              {selectedProject ? (
              <section className="rounded-[8px] border border-slate-200 bg-white p-5 shadow-[0_10px_24px_rgba(15,23,42,0.04)]">
                {selectedProject ? (
                  <div className="flex flex-col gap-5">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <ProjectStatusBadge status={selectedProject.status} />
                        <h2 className="mt-3 text-2xl font-light text-[#243245]">{selectedProject.name}</h2>
                        <p className="mt-1 text-sm text-[#75839a]">{selectedProject.public_id}</p>
                      </div>
                      <Link className="inline-flex h-9 w-9 items-center justify-center rounded-[4px] border border-[#cfd6df] text-[#435067]" title="Edit" to={`/projects/${selectedProject.public_id}/edit`}>
                        <EditIcon fontSize="small" />
                      </Link>
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                      <div className="rounded-[6px] bg-[#f7f8f6] p-3"><p className="text-xs uppercase text-[#75839a]">Opted In</p><p className="text-xl text-[#243245]">{selectedProject.counts?.opted_in || 0}</p></div>
                      <div className="rounded-[6px] bg-[#f7f8f6] p-3"><p className="text-xs uppercase text-[#75839a]">Withdrawn</p><p className="text-xl text-[#243245]">{selectedProject.counts?.withdrawn || 0}</p></div>
                      <div className="rounded-[6px] bg-[#f7f8f6] p-3"><p className="text-xs uppercase text-[#75839a]">Total</p><p className="text-xl text-[#243245]">{selectedProject.counts?.total || 0}</p></div>
                    </div>

                    <div className="grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
                      <p><span className="font-semibold text-[#4d5968]">Application:</span> {formatDate(selectedProject.application_open_at)}</p>
                      <p><span className="font-semibold text-[#4d5968]">Starts:</span> {formatDate(selectedProject.starts_at)}</p>
                      <p><span className="font-semibold text-[#4d5968]">Ends:</span> {formatDate(selectedProject.ends_at)}</p>
                      <p><span className="font-semibold text-[#4d5968]">Criteria:</span> {selectedProject.criteria_summary || "Not set"}</p>
                      <p><span className="font-semibold text-[#4d5968]">Data:</span> {(selectedProject.data_scope || []).join(", ") || "Not set"}</p>
                      <p><span className="font-semibold text-[#4d5968]">Enrollment:</span> {selectedProject.requires_admin_approval !== false ? "Administrator approval required" : "Eligible growers join immediately"}</p>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {selectedProject.status !== "ACTIVE" ? (
                        <button className="inline-flex min-h-[40px] items-center gap-2 rounded-[4px] bg-[#34a853] px-4 text-sm font-semibold text-white disabled:bg-[#c9ced6]" disabled={isSaving} onClick={() => runAction("publish", "Project published.")} type="button">
                          <CheckCircleIcon fontSize="small" />
                          Publish
                        </button>
                      ) : null}
                      {selectedProject.status !== "CLOSED" ? (
                        <button className="inline-flex min-h-[40px] items-center gap-2 rounded-[4px] border border-[#b9c1cc] bg-white px-4 text-sm font-semibold text-[#344054] disabled:bg-[#f2f4f7]" disabled={isSaving} onClick={() => runAction("close", "Project closed.")} type="button">
                          <StopCircleIcon fontSize="small" />
                          Close
                        </button>
                      ) : null}
                    </div>

                    <div className="grid gap-4 xl:grid-cols-[minmax(0,1.2fr)_minmax(320px,0.8fr)]">
                    <div className="rounded-[8px] border border-slate-200 bg-[#fbfcfd] p-4">
                      <div className="flex flex-col gap-3">
                        <div className="flex items-start justify-between gap-3">
                          <p className="text-sm font-semibold uppercase tracking-[0.14em] text-[#75839a]">
                            Data Export
                          </p>
                          <div className="flex flex-wrap gap-2">
                            <button
                              className="inline-flex min-h-[36px] items-center gap-2 rounded-[4px] border border-[#b9c1cc] bg-white px-3 text-sm font-semibold text-[#344054] disabled:bg-[#f2f4f7]"
                              disabled={dataState !== "" || selectedDataScope.length === 0}
                              onClick={refreshDataPreview}
                              type="button"
                            >
                              <RefreshIcon fontSize="small" />
                              Preview
                            </button>
                            <button
                              className="inline-flex min-h-[36px] items-center gap-2 rounded-[4px] bg-[#00adee] px-3 text-sm font-semibold text-white disabled:bg-[#c9ced6]"
                              disabled={dataState !== "" || selectedDataScope.length === 0}
                              onClick={exportProjectData}
                              type="button"
                            >
                              <FileDownloadIcon fontSize="small" />
                              {dataState === "export" ? "Exporting..." : "Excel"}
                            </button>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                          {EXPORTABLE_SCOPE_OPTIONS.map((option) => {
                            const projectScope = selectedProject.data_scope || [];
                            const projectAllowsScope = projectScope.length === 0 || projectScope.includes(option.value);
                            return (
                              <label
                                className={`flex min-h-[40px] items-center gap-2 rounded-[4px] border px-3 text-sm font-semibold ${
                                  selectedDataScope.includes(option.value)
                                    ? "border-[#34a853] bg-[#eef8f0] text-[#1f6d38]"
                                    : "border-[#d7dde6] bg-white text-[#586579]"
                                } ${projectAllowsScope ? "" : "opacity-45"}`}
                                key={option.value}
                              >
                                <input
                                  checked={selectedDataScope.includes(option.value)}
                                  disabled={!projectAllowsScope}
                                  onChange={() => toggleSelectedDataScope(option.value)}
                                  type="checkbox"
                                />
                                {option.label}
                              </label>
                            );
                          })}
                        </div>

                        <Field label="Updated since">
                          <input
                            className={textInputClass()}
                            onChange={(event) => setUpdatedSince(event.target.value)}
                            type="datetime-local"
                            value={updatedSince}
                          />
                        </Field>

                        {dataSummary?.counts ? (
                          <div className="grid grid-cols-2 gap-2">
                            {Object.entries(dataSummary.counts).map(([key, value]) => (
                              <div className="rounded-[6px] bg-white p-3" key={key}>
                                <p className="text-xs uppercase text-[#75839a]">{key.replace("_", " ")}</p>
                                <p className="text-xl text-[#243245]">{value}</p>
                              </div>
                            ))}
                          </div>
                        ) : null}

                        {syncPreview?.data ? (
                          <pre className="max-h-[220px] overflow-auto rounded-[6px] bg-[#243245] p-3 text-xs text-white">
                            {JSON.stringify(syncPreview.data, null, 2)}
                          </pre>
                        ) : null}
                      </div>
                    </div>

                    <div className="rounded-[8px] border border-slate-200 bg-[#fbfcfd] p-4">
                      <p className="text-sm font-semibold uppercase tracking-[0.14em] text-[#75839a]">Participants</p>
                      <p className="mt-1 text-xs leading-5 text-[#75839a]">Only administrator-approved enrollments appear here or in project exports.</p>
                      <div className="mt-3 flex max-h-[320px] flex-col gap-2 overflow-auto">
                        {Array.isArray(selectedProject.participants) && selectedProject.participants.length > 0 ? (
                          selectedProject.participants.map((participant) => (
                            <div className="rounded-[6px] border border-slate-200 bg-[#fbfcfd] p-3 text-sm" key={participant.participant_public_id}>
                              <div className="flex items-center justify-between gap-3">
                                <p className="font-semibold text-[#243245]">{participant.grower_display_name || participant.participant_public_id}</p>
                                <ProjectStatusBadge status={participant.consent_status} />
                              </div>
                              <p className="mt-1 text-xs text-[#75839a]">{participant.grower_email_domain || "domain unavailable"} - {participant.grower_email_hash}</p>
                            </div>
                          ))
                        ) : (
                          <div className="rounded-[6px] border border-dashed border-[#cfd6df] p-4 text-sm text-[#75839a]">No participants yet.</div>
                        )}
                      </div>
                    </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex min-h-[360px] items-center justify-center rounded-[6px] border border-dashed border-[#cfd6df] text-center text-[#75839a]">Select a project to view details.</div>
                )}
              </section>
              ) : null}
            </div>
          </>
        )}
      </div>
    </Page>
  );
}
